// MAGAZINE kit — shared building blocks for the DETAIL screens (game / book /
// tv / anime) and their season views. The four detail screens share a layout:
// a feature hero, a summary paragraph, then a stack of field groups each headed
// by a skewed poppy sticker label over glass controls. These helpers render
// that vocabulary so each view just assembles its hero + fields and keeps its
// existing data/handlers. Spec: Magazine-Rollout-AllScreens.md (Detail rows).
import { el } from "../dom.js";
import { MAG, magFieldLabel, magGlass, magButton, magFeatureHero, magGlassSquare, magEpisodeRow } from "./kit.js";

// Rotating accent colours for successive field-group stickers, matching the
// mockup's "Played it? (lime) / Got it? (cyan) / Format (yellow) / Rating
// (orange) / Awards (pink) / Notes (violet) / Groups (cyan)" rhythm.
export const FIELD_ACCENTS = ["#b6f23d", "#19d3e8", "#ffd60a", "#ff7a1a", "#ff3d8b", "#8b3dff"];

// The detail feature hero with the standard glass meta bar. `kicker` is the
// "Videogame · 2017 · Genre" line; `sticker` is the status sticker; `figures`
// is an array of { value, label } shown in the glass bar (first one is rendered
// large/lime — typically Rating); `tag` is an optional trailing pill (e.g. top
// award). `onBack` / `onMenu` wire the corners.
export function magDetailHero(item, { kicker, title, sticker, figures = [], tag, onBack, onMenu } = {}) {
	const barChildren = [];
	figures.forEach((f, i) => {
		if (i > 0) barChildren.push(el("div", { style: { width: "1px", alignSelf: "stretch", background: "rgba(255,255,255,.18)" } }));
		barChildren.push(
			el(
				"div",
				{ style: i === 1 ? { flex: "1", minWidth: "0" } : {} },
				el("div", {
					style: {
						fontFamily: "'Anton', sans-serif", fontSize: i === 0 ? "26px" : "18px",
						color: i === 0 ? MAG.yellow : "#fff", lineHeight: ".9", textTransform: "uppercase",
					},
				}, String(f.value)),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "7.5px",
						letterSpacing: ".1em", textTransform: "uppercase", color: i === 0 ? "#fff" : MAG.dim,
						overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
					},
				}, f.label),
			),
		);
	});
	if (tag) {
		barChildren.push(
			el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
					letterSpacing: ".05em", textTransform: "uppercase", color: MAG.lime,
					border: "1px solid rgba(182,242,61,.5)", background: "rgba(182,242,61,.08)",
					borderRadius: "6px", padding: "4px 8px", flex: "none",
				},
			}, tag),
		);
	}

	const bar = barChildren.length
		? magGlass(
			el("div", { style: { display: "flex", alignItems: "center", gap: "12px", width: "100%" } }, ...barChildren),
			{ variant: "over-art", style: { padding: "12px 14px" } },
		)
		: null;

	return magFeatureHero(item, {
		height: "330px",
		kicker,
		title,
		sticker,
		onBack,
		kebab: onMenu ? magGlassSquare(kebabIcon(), { onClick: onMenu, label: "More" }) : null,
		bar,
	});
}

function kebabIcon() {
	const span = el("span", { style: { display: "flex" } });
	span.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>';
	return span;
}

// The summary paragraph under the hero.
export function magSummary(text) {
	if (!text) return null;
	return el("p", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "500", fontSize: "13px",
			lineHeight: "1.55", color: "#cfcfd6", margin: "0", padding: "16px 16px 4px", textWrap: "pretty",
		},
	}, text);
}

// A field group: skewed poppy sticker label (colour by index) + its control.
// `control` is any element (a select row, chip set, rating editor, etc.).
export function magField(label, control, { index = 0, color } = {}) {
	const accent = color ?? FIELD_ACCENTS[index % FIELD_ACCENTS.length];
	return el("div", { style: { padding: "0 16px" } }, magFieldLabel(label, accent), control);
}

// A glass "value ▾" select row (the mockup's status fields). Renders the native
// <select> transparently over a glass row so it stays accessible/native while
// looking like the design. `labelFn(value)` maps option→display.
export function magSelectRow(options, current, onChange, labelFn = (v) => v) {
	const sel = el("select", {
		value: current,
		onchange: (e) => onChange(e.currentTarget.value),
		style: {
			position: "absolute", inset: "0", width: "100%", height: "100%",
			opacity: "0", cursor: "pointer", border: "0", appearance: "none",
		},
	}, ...options.map((opt) => el("option", { value: opt, selected: opt === current }, labelFn(opt))));

	return magGlass(
		el("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between" } },
			el("span", {
				style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", textTransform: "uppercase", color: "#fff" },
			}, labelFn(current)),
			el("span", { style: { color: MAG.dim, fontSize: "14px" } }, "▾"),
			sel,
		),
		{ style: { position: "relative", padding: "12px 14px", marginBottom: "2px" } },
	);
}

// Multi-select pill chips. `selected` is a Set; `onToggle(nextSet)` fires on tap.
export function magChips(available, selected, onToggle, labelFn = (v) => v) {
	const wrap = el("div", { style: { display: "flex", gap: "7px", flexWrap: "wrap" } });
	for (const value of available) {
		const on = selected.has(value);
		wrap.append(el("button", {
			type: "button",
			"aria-pressed": on ? "true" : "false",
			onclick: () => {
				const next = new Set(selected);
				if (next.has(value)) next.delete(value); else next.add(value);
				onToggle(next);
			},
			style: chipStyle(on),
		}, labelFn(value)));
	}
	return wrap;
}

// Single-select pill chips: tap to select, tap selected to clear. Options are
// { value, label }.
export function magSingleChips(options, current, onChange) {
	const wrap = el("div", { style: { display: "flex", gap: "7px", flexWrap: "wrap" } });
	for (const opt of options) {
		const on = current === opt.value;
		wrap.append(el("button", {
			type: "button",
			"aria-pressed": on ? "true" : "false",
			onclick: () => onChange(on ? null : opt.value),
			style: chipStyle(on),
		}, opt.label));
	}
	return wrap;
}

function chipStyle(on) {
	return {
		fontFamily: "'Archivo', sans-serif", fontWeight: on ? "900" : "800", fontSize: "11px",
		letterSpacing: ".04em", textTransform: "uppercase", borderRadius: "999px",
		padding: "7px 14px", cursor: "pointer",
		color: on ? "#0b0b0f" : MAG.dim, background: on ? MAG.lime : "rgba(255,255,255,.07)",
		border: on ? "0" : `1px solid ${MAG.line}`,
	};
}

// Lime star rating editor (1–5). Tapping the current rating clears it. Replaces
// the calm ratingEditor on detail screens.
export function magRating(current, onChange) {
	const wrap = el("div", { style: { display: "flex", gap: "5px", fontSize: "34px", lineHeight: "1", cursor: "pointer" } });
	for (let i = 1; i <= 5; i++) {
		const filled = current != null && i <= current;
		wrap.append(el("span", {
			role: "button",
			"aria-label": `${i} star${i === 1 ? "" : "s"}`,
			onclick: () => onChange(current === i ? null : i),
			style: { color: filled ? MAG.yellow : "rgba(255,255,255,.18)" },
		}, "★"));
	}
	return wrap;
}

// A glass note editor with a character counter.
export function magNote(value, { maxLength = 280, onSave, placeholder } = {}) {
	const counter = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "8.5px",
			letterSpacing: ".06em", textTransform: "uppercase", color: MAG.faint, textAlign: "right", marginTop: "7px",
		},
	}, `${(value ?? "").length} / ${maxLength}`);
	const ta = el("textarea", {
		value: value ?? "",
		maxlength: String(maxLength),
		rows: 3,
		placeholder: placeholder ?? "",
		oninput: (e) => { counter.textContent = `${e.currentTarget.value.length} / ${maxLength}`; },
		onblur: (e) => onSave?.(e.currentTarget.value),
		style: {
			width: "100%", resize: "vertical", background: "transparent", border: "0", outline: "none",
			fontFamily: "'Archivo', sans-serif", fontWeight: "500", fontSize: "13px", color: "#eaeaf0", lineHeight: "1.5",
		},
	});
	return magGlass([ta, counter], { style: { padding: "12px 14px" } });
}

// "More in this series" — shown at the bottom of a detail screen when the item
// belongs to a franchise/series. `entries` = [{ title, cover_url, year, owned }];
// `onAdd(entry)` adds one, `onAddAll()` adds the whole series as a collection.
// Not-owned entries render dimmed (suggestions); owned ones show "✓ Have".
export function magSeriesMore(mediaType, { entries = [], onAdd, onAddAll } = {}) {
	if (!entries.length) return el("div", {});
	const wrap = el("div", { style: { padding: "0 16px", marginTop: "8px" } });
	wrap.append(magFieldLabel("More in this series", "#ff7a1a"));
	if (onAddAll) {
		wrap.append(magButton("+ Add whole series as a collection", {
			variant: "primary", block: true, onClick: onAddAll, style: { marginBottom: "10px" },
		}));
	}
	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px" } });
	entries.forEach((e, i) => {
		list.append(
			magEpisodeRow(
				{ title: e.title, cover_url: e.cover_url, media_type: mediaType },
				{
					number: i + 1,
					kicker: e.year ? String(e.year) : "",
					owned: !!e.owned,
					ownedLabel: "Have",
					addLabel: "+ Add",
					dimmed: !e.owned,
					onAdd: () => onAdd?.(e),
				},
			),
		);
	});
	wrap.append(list);
	return wrap;
}

// A full-width danger delete button row (the detail footer).
export function magDeleteFooter(label, onClick) {
	return el("div", { style: { padding: "20px 16px 24px" } }, magButton(label, { variant: "danger", onClick, block: true }));
}

// The small "added / last engaged" date line under the fields.
export function magDateLine(pairs) {
	return el("div", {
		style: {
			display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px",
			fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px",
			letterSpacing: ".06em", textTransform: "uppercase", padding: "20px 16px 4px", color: MAG.faint,
		},
	}, ...pairs.map(([k, v]) => el("span", {}, `${k} `, el("span", { style: { color: "#cfcfd6" } }, v ?? "—"))));
}
